import React, { useContext, useEffect, useRef, useState } from 'react';
import AdminLayout from '@app/layout/AdminLayout.tsx';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { ApiContext } from '@api/api';
import { RingilHeading } from '@components/styles.tsx';
import RingilDataTable, {
  ColumnConfig,
  FilterConfig,
  FilterType,
  PagedData,
} from '@components/RingilDataTable/RingilDataTable.tsx';
import {
  CargoTemplateDetailOutDTO,
  CargoTemplateFilter,
  CargoTemplateListItemOutDTO,
  CargoTemplateRequestInDTO,
  PageResponseCargoTemplateListItemOutDTO,
  Pageable,
} from '@api/ringil-api.v2.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import RingilDialog from '@components/framework/dialog/RingilDialog.tsx';
import { Field } from '@components/framework/formfield/Field.tsx';
import RingilInputText from '@components/framework/input/RingilInput/RingilInputText.tsx';
import { InputNumber } from '@components/framework/input/InputNumber';
import { Checkbox } from 'primereact/checkbox';
import useForm from '@hooks/useForm/useForm';
import * as yup from 'yup';
import { findValidationMessage } from '@utils/validation.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation.tsx';

type CargoTemplateFormValues = CargoTemplateRequestInDTO;

const DEFAULT_EW_MAPPING = 'pallets';
const DEFAULT_DACHSER_MAPPING = 'EW';
const DEFAULT_SCHENKER_MAPPING = 'ZZ';

const emptyForm: CargoTemplateFormValues = {
  name: '',
  width: undefined,
  height: undefined,
  length: undefined,
  weight: undefined,
  count: 1,
  stackable: false,
  disabled: false,
  code: '',
  order: 0,
  ewMapping: DEFAULT_EW_MAPPING,
  dachserMapping: DEFAULT_DACHSER_MAPPING,
  decimalSpaces: 0,
  schenkerMapping: DEFAULT_SCHENKER_MAPPING,
};

const nameKeyFromCode = (code: string): string =>
  code ? `cargoItemType.db.${code.toLowerCase()}` : '';

interface CargoTemplateFormDialogProps {
  visible: boolean;
  cargoTemplateId: string | null;
  onHide: () => void;
  onSaved: () => void;
}

const CargoTemplateFormDialog: React.FC<CargoTemplateFormDialogProps> = ({ visible, cargoTemplateId, onHide, onSaved }) => {
  const { tr } = useTranslationLgs();
  const {
    adminGetCargoTemplate,
    adminCreateCargoTemplate,
    adminEditCargoTemplate,
    adminGetNextOrderCargoTemplate,
  } = useContext(ApiContext);
  const [saving, setSaving] = useState(false);
  const [userTouchedName, setUserTouchedName] = useState(false);

  const schema = yup.object().shape({
    name: yup.string().required(),
    code: yup.string().required(),
    order: yup.number().required(),
    decimalSpaces: yup.number().required(),
    count: yup.number().required(),
  });

  const form = useForm<CargoTemplateFormValues>(
    schema,
    emptyForm,
    () => handleSave(),
    false,
    false,
  );

  useEffect(() => {
    if (!visible) return;
    if (cargoTemplateId) {
      setUserTouchedName(true);
      adminGetCargoTemplate(cargoTemplateId, (data: CargoTemplateDetailOutDTO) => {
        form.setValues({
          name: data.name,
          width: data.width,
          height: data.height,
          length: data.length,
          weight: data.weight,
          count: data.count,
          stackable: data.stackable,
          disabled: data.disabled,
          code: data.code,
          order: data.order ?? 0,
          ewMapping: data.ewMapping,
          dachserMapping: data.dachserMapping,
          decimalSpaces: data.decimalSpaces,
          schenkerMapping: data.schenkerMapping,
        });
      });
    } else {
      setUserTouchedName(false);
      form.setValues(emptyForm);
      adminGetNextOrderCargoTemplate(({ nextOrder }) => {
        form.setFieldValue('order', nextOrder);
      });
    }
  }, [visible, cargoTemplateId]);

  useEffect(() => {
    if (!visible || cargoTemplateId || userTouchedName) return;
    form.setFieldValue('name', nameKeyFromCode(form.values.code));
  }, [visible, cargoTemplateId, userTouchedName, form.values.code]);

  const handleSave = () => {
    setSaving(true);
    const body: CargoTemplateRequestInDTO = { ...form.values };
    const done = () => {
      setSaving(false);
      onSaved();
      onHide();
    };
    if (cargoTemplateId) {
      adminEditCargoTemplate(cargoTemplateId, body, done, null, {
        onError: (_error, showStandardError) => {
          setSaving(false);
          showStandardError();
        },
        onValidationFailed: () => setSaving(false),
      });
    } else {
      adminCreateCargoTemplate(body, done, null, {
        onError: (_error, showStandardError) => {
          setSaving(false);
          showStandardError();
        },
        onValidationFailed: () => setSaving(false),
      });
    }
  };

  return (
    <RingilDialog
      visible={visible}
      onHide={onHide}
      wide
      heading={cargoTemplateId ? `Edit cargo template` : `Create cargo template`}
      buttons={
        <>
          <RingilButton label={'Save'} mode={'filled'} onClick={() => form.validateAndSend()} loading={saving} />
          <RingilButton label={'Cancel'} mode={'outlined'} onClick={onHide} />
        </>
      }
    >
      <div className={'grid grid-cols-2 gap-4 w-[680px] max-h-[70vh] overflow-y-auto pr-2'}>
        <Field
          label={'Code'}
          required={!form.values.code}
          errorMessage={findValidationMessage(form.validationErrors, form.names.code, tr)}
        >
          <RingilInputText value={form.values.code} onChange={v => form.setFieldValue('code', v)} fullWidth />
        </Field>

        <Field
          label={'Name (i18n key)'}
          required={!form.values.name}
          errorMessage={findValidationMessage(form.validationErrors, form.names.name, tr)}
        >
          <RingilInputText
            value={form.values.name}
            onChange={v => {
              setUserTouchedName(true);
              form.setFieldValue('name', v);
            }}
            fullWidth
          />
        </Field>

        <Field
          label={'Order'}
          required={form.values.order === undefined || form.values.order === null}
          errorMessage={findValidationMessage(form.validationErrors, form.names.order, tr)}
        >
          <InputNumber
            value={form.values.order ?? null}
            onValueChange={e => form.setFieldValue('order', (e.value ?? 0) as number)}
            useGrouping={false}
          />
        </Field>

        <Field
          label={'Count'}
          required={form.values.count === undefined || form.values.count === null}
          errorMessage={findValidationMessage(form.validationErrors, form.names.count, tr)}
        >
          <InputNumber
            value={form.values.count ?? null}
            onValueChange={e => form.setFieldValue('count', (e.value ?? 0) as number)}
            useGrouping={false}
          />
        </Field>

        <Field
          label={'Decimal spaces'}
          required={form.values.decimalSpaces === undefined || form.values.decimalSpaces === null}
          errorMessage={findValidationMessage(form.validationErrors, form.names.decimalSpaces, tr)}
        >
          <InputNumber
            value={form.values.decimalSpaces ?? null}
            onValueChange={e => form.setFieldValue('decimalSpaces', (e.value ?? 0) as number)}
            useGrouping={false}
          />
        </Field>

        <div />

        <Field label={'Width'}>
          <InputNumber
            value={form.values.width ?? null}
            onValueChange={e => form.setFieldValue('width', (e.value ?? null) as number | null)}
            mode={'decimal'}
            minFractionDigits={0}
            maxFractionDigits={3}
            useGrouping={false}
          />
        </Field>

        <Field label={'Height'}>
          <InputNumber
            value={form.values.height ?? null}
            onValueChange={e => form.setFieldValue('height', (e.value ?? null) as number | null)}
            mode={'decimal'}
            minFractionDigits={0}
            maxFractionDigits={3}
            useGrouping={false}
          />
        </Field>

        <Field label={'Length'}>
          <InputNumber
            value={form.values.length ?? null}
            onValueChange={e => form.setFieldValue('length', (e.value ?? null) as number | null)}
            mode={'decimal'}
            minFractionDigits={0}
            maxFractionDigits={3}
            useGrouping={false}
          />
        </Field>

        <Field label={'Weight'}>
          <InputNumber
            value={form.values.weight ?? null}
            onValueChange={e => form.setFieldValue('weight', (e.value ?? null) as number | null)}
            mode={'decimal'}
            minFractionDigits={0}
            maxFractionDigits={3}
            useGrouping={false}
          />
        </Field>

        <div className={'col-span-2 text-sm text-gray-600 italic mt-2'}>
          EW / Dachser / Schenker mapping values must match the enums used by the corresponding external systems.
        </div>

        <Field label={'EW mapping'}>
          <RingilInputText
            value={form.values.ewMapping ?? ''}
            onChange={v => form.setFieldValue('ewMapping', v || undefined)}
            fullWidth
          />
        </Field>

        <Field label={'Dachser mapping'}>
          <RingilInputText
            value={form.values.dachserMapping ?? ''}
            onChange={v => form.setFieldValue('dachserMapping', v || undefined)}
            fullWidth
          />
        </Field>

        <Field label={'Schenker mapping'}>
          <RingilInputText
            value={form.values.schenkerMapping ?? ''}
            onChange={v => form.setFieldValue('schenkerMapping', v || undefined)}
            fullWidth
          />
        </Field>

        <div />

        <div className={'flex flex-row items-center gap-6 mt-4 col-span-2'}>
          <label className={'flex flex-row items-center gap-2 cursor-pointer'}>
            <Checkbox
              checked={form.values.stackable}
              onChange={e => form.setFieldValue('stackable', !!e.checked)}
            />
            <span>Stackable</span>
          </label>

          <label className={'flex flex-row items-center gap-2 cursor-pointer'}>
            <Checkbox
              checked={form.values.disabled}
              onChange={e => form.setFieldValue('disabled', !!e.checked)}
            />
            <span>Disabled (soft delete)</span>
          </label>
        </div>
      </div>
    </RingilDialog>
  );
};

const IndexPage: React.FC = () => {
  const { adminListCargoTemplate } = useContext(ApiContext);
  const [dialogVisible, setDialogVisible] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const tableRef = useRef<{ reloadData: () => void; selectedObjects: unknown[]; clear: () => void }>(null);

  const loadData = (
    filter: CargoTemplateFilter,
    pageable: Pageable,
    onSuccess: (pagedData: PagedData<CargoTemplateListItemOutDTO>) => void,
  ) => {
    adminListCargoTemplate(pageable, filter, (response: PageResponseCargoTemplateListItemOutDTO) => {
      onSuccess({ data: response.data, total: response.total, page: response.page, size: response.size });
    });
  };

  const openCreate = () => {
    setEditingId(null);
    setDialogVisible(true);
  };

  const openEdit = (row: CargoTemplateListItemOutDTO) => {
    setEditingId(row.id);
    setDialogVisible(true);
  };

  const onSaved = () => {
    tableRef.current?.reloadData();
  };

  const columnsConfig: ColumnConfig<CargoTemplateListItemOutDTO>[] = [
    { id: 'name', header: 'admin.tools.cargoTemplates.column.name', headerDefault: 'Name', width: '35%', sortable: false, field: 'name', order: 1, render: row => <div>{row.name}</div> },
    { id: 'code', header: 'admin.tools.cargoTemplates.column.code', headerDefault: 'Code', width: '25%', sortable: false, field: 'code', order: 2, render: row => <div>{row.code}</div> },
    { id: 'order', header: 'admin.tools.cargoTemplates.column.order', headerDefault: 'Order', width: '10%', sortable: false, field: 'order', order: 3, render: row => <div>{row.order}</div> },
    { id: 'stackable', header: 'admin.tools.cargoTemplates.column.stackable', headerDefault: 'Stackable', width: '15%', sortable: false, field: 'stackable', order: 4, render: row => <div>{row.stackable ? 'Yes' : 'No'}</div> },
    {
      id: 'actions',
      header: 'admin.tools.cargoTemplates.column.actions',
      headerDefault: '',
      width: '15%',
      sortable: false,
      order: 5,
      render: row => (
        <RingilButton label={'Edit'} size={'small'} mode={'table'} onClick={() => openEdit(row)} />
      ),
    },
  ];

  const filtersConf: FilterConfig[] = [
    { filterType: FilterType.InputText, placeholder: 'Filter by code', value: ['code'] },
  ];

  return (
    <AdminLayout title={'Cargo templates'}>
      <RingilHeading>Cargo templates</RingilHeading>

      <div className={'flex flex-row justify-end mt-4'}>
        <RingilButton label={'Create'} mode={'filled'} onClick={openCreate} />
      </div>

      <div className={'mt-4'}>
        <RingilDataTable
          ref={tableRef}
          columnConfig={columnsConfig as ColumnConfig<unknown>[]}
          loadDataFromApi={loadData}
          filterName={'admin_cargo_templates_list'}
          filtersConf={filtersConf}
        />
      </div>

      <CargoTemplateFormDialog
        visible={dialogVisible}
        cargoTemplateId={editingId}
        onHide={() => setDialogVisible(false)}
        onSaved={onSaved}
      />
    </AdminLayout>
  );
};

export default withAuthenticationRequired(IndexPage);
